/**
 * The first-run tour: a spotlight on one element at a time with a short
 * card beside it. Shown once per person — the step reached is written to
 * their platform profile as they go, so a tour left half-way resumes where
 * it stopped on any device, and "Done" or "Skip" ends it for good.
 *
 * No library: a fixed overlay whose box-shadow darkens everything but the
 * target's rectangle, re-measured on resize and scroll. A step whose target
 * the person cannot see shows its card centred instead (tourTarget).
 *
 * @module components/onboarding/AppTour
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { updateAccountMetadata, useAccountProfile } from "../../state/accountProfile";
import { useOrgs } from "../../state/orgs";
import { Button } from "../ui/button";
import { readTourState, resumeStep, TOUR_STEPS, type TourStep } from "./tourSteps";
import { measureTourTarget, type TourRect } from "./tourTarget";

const SPOTLIGHT_PADDING = 6;
const CARD_WIDTH = 320;
const CARD_GAP = 12;

/** Card position for a target rect, kept inside the viewport. */
function cardPosition(step: TourStep, rect: TourRect | null): { top: number; left: number } {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  if (rect === null) {
    return { top: Math.max(24, viewportHeight / 2 - 120), left: Math.max(16, viewportWidth / 2 - CARD_WIDTH / 2) };
  }
  let top: number;
  let left: number;
  switch (step.placement) {
    case "right":
      top = rect.top;
      left = rect.left + rect.width + CARD_GAP + SPOTLIGHT_PADDING;
      break;
    case "top":
      top = rect.top - CARD_GAP - SPOTLIGHT_PADDING - 200;
      left = rect.left + rect.width / 2 - CARD_WIDTH / 2;
      break;
    default:
      top = rect.top + rect.height + CARD_GAP + SPOTLIGHT_PADDING;
      left = rect.left + rect.width / 2 - CARD_WIDTH / 2;
  }
  left = Math.min(Math.max(16, left), viewportWidth - CARD_WIDTH - 16);
  top = Math.min(Math.max(16, top), viewportHeight - 16 - 200);
  return { top, left };
}

export function AppTour() {
  const { account, isPending } = useAccountProfile();
  const { available } = useOrgs();
  const stored = useMemo(() => readTourState(account?.metadata ?? null), [account?.metadata]);
  const [step, setStep] = useState<number | null>(null);
  const [rect, setRect] = useState<TourRect | null>(null);
  const started = useRef(false);

  // Start once the account is known and the tour has not been completed.
  useEffect(() => {
    if (started.current || isPending || account === null || !available) return;
    const resume = resumeStep(stored);
    if (resume === null) return;
    started.current = true;
    setStep(resume);
  }, [account, available, isPending, stored]);

  const current = step === null ? null : (TOUR_STEPS[step] ?? null);

  useLayoutEffect(() => {
    if (current === null) return;
    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setRect(measureTourTarget(current.target)));
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    // The target may mount a moment after the route settles.
    const retry = window.setTimeout(update, 300);
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(retry);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [current]);

  const persist = useCallback((patch: Record<string, unknown>) => {
    void updateAccountMetadata({ tour: patch });
  }, []);

  const finish = useCallback(() => {
    setStep(null);
    persist({ step: TOUR_STEPS.length, completedAt: new Date().toISOString() });
  }, [persist]);

  const next = useCallback(() => {
    if (step === null) return;
    const following = step + 1;
    if (following >= TOUR_STEPS.length) {
      finish();
      return;
    }
    setStep(following);
    persist({ step: following });
  }, [finish, persist, step]);

  const back = useCallback(() => {
    if (step === null || step === 0) return;
    setStep(step - 1);
    persist({ step: step - 1 });
  }, [persist, step]);

  useEffect(() => {
    if (current === null) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") finish();
      if (event.key === "ArrowRight" || event.key === "Enter") next();
      if (event.key === "ArrowLeft") back();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [back, current, finish, next]);

  if (current === null || step === null) return null;
  const position = cardPosition(current, rect);
  const last = step === TOUR_STEPS.length - 1;

  return (
    <div className="fixed inset-0 z-[80]" role="dialog" aria-modal="true" aria-labelledby="app-tour-title">
      {rect ? (
        <div
          aria-hidden
          className="pointer-events-none fixed rounded-lg ring-2 ring-primary/70 transition-[top,left,width,height] duration-200"
          style={{
            top: rect.top - SPOTLIGHT_PADDING,
            left: rect.left - SPOTLIGHT_PADDING,
            width: rect.width + SPOTLIGHT_PADDING * 2,
            height: rect.height + SPOTLIGHT_PADDING * 2,
            boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.55)",
          }}
        />
      ) : (
        <div aria-hidden className="fixed inset-0 bg-black/55" />
      )}
      <div
        className="fixed flex flex-col gap-3 rounded-xl border border-border bg-popover p-4 text-popover-foreground shadow-xl"
        style={{ top: position.top, left: position.left, width: CARD_WIDTH }}
      >
        <div className="flex items-baseline justify-between gap-3">
          <h2 id="app-tour-title" className="text-sm font-semibold">
            {current.title}
          </h2>
          <span className="shrink-0 text-[11px] text-muted-foreground tabular-nums">
            {step + 1} / {TOUR_STEPS.length}
          </span>
        </div>
        <p className="text-sm leading-relaxed text-muted-foreground">{current.body}</p>
        <div className="flex items-center justify-between gap-2 pt-1">
          <Button type="button" variant="ghost" size="sm" onClick={finish} className="text-muted-foreground">
            Skip tour
          </Button>
          <div className="flex items-center gap-2">
            {step > 0 ? (
              <Button type="button" variant="outline" size="sm" onClick={back}>
                Back
              </Button>
            ) : null}
            <Button type="button" size="sm" onClick={next} autoFocus>
              {last ? "Done" : "Next"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
